/**
 * rackFlyout.logic.ts — Pure functional domain logic for RackFlyoutDrawer and DcViewer.
 *
 * Implements:
 * - Mini-elevation unit grouping and descending slot generation (U_total down to U1).
 * - Multi-U equipment and reservation collision-safe rendering slices.
 * - Power, weight, and space metrics aggregation with safe defensive calculations.
 * - Canonical shareable link formatting.
 */

import type { SpaceMetrics, RackReservation, EquipmentSlotItem } from './rackReservation.logic.ts';
import { getReservationUnitRange } from './rackReservation.logic.ts';
import { calculateRackMetrics, isZeroUEquipment, summarizeEquipmentRating } from './rackMetrics.logic.ts';

export const EQUIPMENT_TYPE_COLORS: Record<string, string> = {
  server: '#00d2ff',
  switch: '#9d4edd',
  storage: '#ff9e00',
  firewall: '#ff007f',
  router: '#39ff14',
  pdu: '#ff003c',
  ups: '#ffb703',
  patch_panel: '#00f5d4',
  default: '#60a5fa',
};

export interface ElevationSlot {
  uStart: number;
  uEnd: number;
  heightU: number;
  type: 'equipment' | 'reservation' | 'empty';
  label: string;
  equipment?: any;
  reservation?: RackReservation;
  color?: string;
}

export interface RackFlyoutMetrics {
  space: SpaceMetrics;
  totalPowerW: number;
  maxPowerW: number | null;
  powerLoadPct: number | null;
  totalWeightKg: number;
  maxWeightKg: number | null;
  weightLoadPct: number | null;
  zeroUEquipment: any[];
}

/**
 * Computes mini-elevation slots descending from totalU down to 1.
 * Groups contiguous multi-U devices and reservations into cohesive visual blocks.
 */
export function computeMiniElevationSlots(
  totalU: number,
  equipmentList: EquipmentSlotItem[] = [],
  reservations: RackReservation[] = []
): ElevationSlot[] {
  const maxU = Number.isFinite(totalU) ? Math.max(0, Math.floor(totalU)) : 0;

  // Map each unit to equipment or reservation
  const unitEquipmentMap = new Map<number, EquipmentSlotItem>();
  for (const eq of equipmentList) {
    if (isZeroUEquipment(eq) || eq.u_position == null) continue;
    const pos = Number(eq.u_position);
    if (!Number.isFinite(pos)) continue;
    const start = Math.floor(pos);
    const height = Math.max(1, Math.floor(Number(eq.u_height) || 1));
    const end = start + height - 1;
    for (let u = Math.max(1, start); u <= Math.min(maxU, end); u++) {
      if (u >= 1 && u <= maxU) {
        unitEquipmentMap.set(u, eq);
      }
    }
  }

  const unitResMap = new Map<number, RackReservation>();
  for (const res of reservations) {
    const { start, end } = getReservationUnitRange(res);
    for (let u = Math.max(1, start); u <= Math.min(maxU, end); u++) {
      if (u >= 1 && u <= maxU && !unitEquipmentMap.has(u)) {
        unitResMap.set(u, res);
      }
    }
  }

  const slots: ElevationSlot[] = [];
  let u = maxU;

  while (u >= 1) {
    if (unitEquipmentMap.has(u)) {
      const eq = unitEquipmentMap.get(u)!;
      const end = u;
      let start = u;
      while (start > 1 && unitEquipmentMap.get(start - 1) === eq) start--;
      const spanHeight = end - start + 1;
      const eqType = (eq as any).equipment_type?.toLowerCase() || 'server';
      const color = EQUIPMENT_TYPE_COLORS[eqType] || EQUIPMENT_TYPE_COLORS.default;

      slots.push({
        uStart: start,
        uEnd: end,
        heightU: spanHeight,
        type: 'equipment',
        label: eq.name || `Прибор (${spanHeight}U)`,
        equipment: eq,
        color,
      });

      u = start - 1;
    } else if (unitResMap.has(u)) {
      const res = unitResMap.get(u)!;
      const clampEnd = u;
      let clampStart = u;
      while (clampStart > 1 && unitResMap.get(clampStart - 1) === res) clampStart--;
      const spanHeight = clampEnd - clampStart + 1;

      slots.push({
        uStart: clampStart,
        uEnd: clampEnd,
        heightU: spanHeight,
        type: 'reservation',
        label: res.description || res.tenant || 'Резерв',
        reservation: res,
        color: '#f59e0b',
      });

      u = clampStart - 1;
    } else {
      slots.push({
        uStart: u,
        uEnd: u,
        heightU: 1,
        type: 'empty',
        label: `U${u}`,
      });
      u -= 1;
    }
  }

  return slots;
}

/**
 * Aggregates space, power, and weight utilization metrics.
 */
export function computeRackUtilizationMetrics(
  totalU: number,
  equipmentList: any[] = [],
  reservations: RackReservation[] = [],
  maxPowerW?: number | null,
  maxWeightKg?: number | null
): RackFlyoutMetrics {
  const space = calculateRackMetrics(equipmentList, reservations, totalU);

  const power = summarizeEquipmentRating(equipmentList, 'power_w');
  const weight = summarizeEquipmentRating(equipmentList, 'weight_kg');
  const totalPowerW = power.total;
  const totalWeightKg = weight.total;
  const zeroUEquipment = equipmentList.filter(isZeroUEquipment);

  const numMaxP = Number(maxPowerW);
  const safeMaxP = maxPowerW != null && Number.isFinite(numMaxP) && numMaxP >= 0 ? numMaxP : null;
  const numMaxW = Number(maxWeightKg);
  const safeMaxW = maxWeightKg != null && Number.isFinite(numMaxW) && numMaxW >= 0 ? numMaxW : null;

  const powerLoadPct = safeMaxP == null || safeMaxP === 0 || power.unknownCount ? null : Math.round((totalPowerW / safeMaxP) * 100);
  const weightLoadPct = safeMaxW == null || safeMaxW === 0 || weight.unknownCount ? null : Math.round((totalWeightKg / safeMaxW) * 100);

  return {
    space,
    totalPowerW,
    maxPowerW: safeMaxP,
    powerLoadPct,
    totalWeightKg,
    maxWeightKg: safeMaxW,
    weightLoadPct,
    zeroUEquipment,
  };
}

/**
 * Generates canonical passport URL for rack sharing.
 */
export function formatShareableRackUrl(origin: string, dcCode: string, rackId: number | string): string {
  const cleanOrigin = (origin || '').replace(/\/+$/, '');
  const cleanDc = encodeURIComponent(String(dcCode || '').trim());
  const cleanId = encodeURIComponent(String(rackId ?? '').trim());
  return `${cleanOrigin}/${cleanDc}/racks/${cleanId}`;
}

export function formatViewerRackUrl(origin: string, dcCode: string, rackId: number | string): string {
  const cleanOrigin = (origin || '').replace(/\/+$/, '');
  const cleanDc = encodeURIComponent(String(dcCode || '').trim());
  const cleanId = encodeURIComponent(String(rackId ?? '').trim());
  return `${cleanOrigin}/${cleanDc}/map?rack=${cleanId}`;
}
